import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useBusinessSetupStatus, type SectionLoadState } from '../../../hooks/useBusinessSetupStatus';
import { apiService } from '../../../services/api';
import { queryKeys } from '../../../lib/queryKeys';
import { legalDocumentUrl } from '../../../lib/legalLinks';
import sectionStyles from './sections/Section.module.css';
import styles from './GoLive.module.css';

type ChecklistKey = 'businessDetails' | 'pinnedBranch' | 'categories' | 'services' | 'staff';

/**
 * A checklist row is only `todo` when the data actually says so. `checking` and
 * `unknown` (the request failed) keep the row honest instead of telling the owner
 * to complete something that may already be done.
 */
type ItemState = 'done' | 'todo' | 'checking' | 'unknown';

const ITEM_STATE_KEY: Record<ItemState, string> = {
    done: 'mybiz.submit.itemDone',
    todo: 'mybiz.submit.itemTodo',
    checking: 'mybiz.submit.itemChecking',
    unknown: 'mybiz.submit.itemUnknown',
};

/** Decorative only — the state is announced by the screen-reader-only label. Non-directional (RTL). */
const ITEM_STATE_ICON: Record<ItemState, string> = { done: '✓', todo: '', checking: '…', unknown: '!' };

/** The operational setup a bookable pilot salon needs before it is submitted. */
const CHECKLIST: { key: ChecklistKey; labelKey: string }[] = [
    { key: 'businessDetails', labelKey: 'mybiz.submit.item.businessDetails' },
    { key: 'pinnedBranch', labelKey: 'mybiz.submit.item.branches' },
    { key: 'categories', labelKey: 'mybiz.submit.item.categories' },
    { key: 'services', labelKey: 'mybiz.submit.item.services' },
    // Batch B (AB#2176, G-12) — a salon is not bookable without staff (a login is optional).
    { key: 'staff', labelKey: 'mybiz.submit.item.staff' },
];

/** A usable map pin: both coordinates present, in range, and not the 0,0 default. */
function hasMapPin(latitude?: number | null, longitude?: number | null): boolean {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') return false;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
    if (latitude === 0 && longitude === 0) return false;
    return Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
}

/**
 * AB#2108 (Admin V1 P0-2) — founder-controlled go-live, step 2: "Submit to
 * Devanza". Shown while the business is a Draft, or Rejected (to resubmit after
 * the founder asked for changes). `POST /onboarding/submit` moves it to
 * PendingApproval; a Devanza founder then approves it in Pending Approvals.
 * There is deliberately no owner "go live" action.
 *
 * Enabled only when the setup checklist is complete — including an active branch
 * WITH a map pin (customers find salons by branch location, and the founder's
 * approval page does not show branches) — and the owner confirms the details and
 * accepts the Terms & Conditions and Privacy Policy, linked here (pilot gate
 * AB#2110). That is what the backend's termsAccepted / privacyAccepted mean.
 */
const SubmitToDevanzaCard: React.FC = () => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const { businessId } = useAuthContext();
    const { businessStatus, statusUnavailable, refreshOnboardingStatus } = useOnboarding();
    const { sectionsCompleted, sectionsState, retryFailedSections } = useBusinessSetupStatus();
    // Same key + fetcher as the setup-status dots, so the query is shared, not refetched.
    const branchQuery = useQuery({
        queryKey: queryKeys.branches.list(),
        queryFn: () => apiService.getBranches(),
        enabled: businessId != null,
        staleTime: 60_000,
        retry: 1,
    });
    // Batch B (AB#2176, G-12) — at least one active staff member (with or without a login).
    // The backend applies the full rule (an active service at an active branch) on submit.
    const staffQuery = useQuery({
        queryKey: queryKeys.staff.list(),
        queryFn: () => apiService.getBusinessStaff({ page: 1, pageSize: 50 }), // API maximum is 50
        enabled: businessId != null,
        staleTime: 60_000,
        retry: 1,
    });
    const [consent, setConsent] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const inFlight = useRef(false);

    // Once PendingApproval is known, the owner-wide banner carries the state.
    if (businessStatus === 'pending_approval') return null;

    if (submitted) {
        // Submitted in this session: keep the confirmation visible even if the
        // status refresh failed (the status is then unavailable, so neither this
        // card nor the owner-wide banner could say where the submission stands).
        return (
            <section className={sectionStyles.card} role="status">
                <p className={sectionStyles.cardSub}>{t('approval.banner.pending')}</p>
            </section>
        );
    }

    // `statusUnavailable` — the status request failed, so the card stays visible with a
    // retry instead of vanishing on a transient error; Submit stays disabled meanwhile.
    if (businessStatus !== 'draft' && businessStatus !== 'rejected' && !statusUnavailable) return null;

    // The branch item is computed ONLY from loaded branch data. Until the request
    // succeeds the row is "checking"; if it failed it is "unknown" with a retry,
    // so a transport problem is never shown as missing setup — and Submit stays
    // disabled either way, because readiness is not known yet.
    const branchState: ItemState = branchQuery.isError
        ? 'unknown'
        : branchQuery.isSuccess
            ? ((branchQuery.data?.branches ?? []).some(b => b.active && hasMapPin(b.latitude, b.longitude)) ? 'done' : 'todo')
            : 'checking';

    const staffState: ItemState = staffQuery.isError
        ? 'unknown'
        : staffQuery.isSuccess
            ? ((staffQuery.data?.staff ?? []).some(s => s.isActive) ? 'done' : 'todo')
            : 'checking';

    /** A row is only "to do" once its own source has answered and said so. */
    const rowState = (completed: boolean, load: SectionLoadState): ItemState =>
        load === 'failed' ? 'unknown' : load === 'checking' ? 'checking' : completed ? 'done' : 'todo';

    const itemState: Record<ChecklistKey, ItemState> = {
        businessDetails: rowState(sectionsCompleted.businessDetails, sectionsState.businessDetails),
        pinnedBranch: branchState,
        categories: rowState(sectionsCompleted.categories, sectionsState.categories),
        services: rowState(sectionsCompleted.services, sectionsState.services),
        staff: staffState,
    };
    const states = CHECKLIST.map(item => itemState[item.key]);
    const ready = states.every(state => state === 'done');
    const checkFailed = statusUnavailable || states.includes('unknown');
    const checking = !checkFailed && states.includes('checking');
    const canSubmit = ready && consent && businessId != null && !saving && !statusUnavailable;
    // While a check is running or after one failed, the generic "complete the items
    // above" hint would be misleading: the checking / failed message explains it.
    const saveHintKey = checking
        ? 'mybiz.submit.checking'
        : checkFailed || ready
            ? ''
            : 'mybiz.submit.incomplete';

    /** Recover in place: refetch only what failed, the status itself included. */
    const retryChecks = () => {
        retryFailedSections();
        if (branchQuery.isError) void branchQuery.refetch();
        if (staffQuery.isError) void staffQuery.refetch();
        if (statusUnavailable) void refreshOnboardingStatus();
    };

    const handleSubmit = async () => {
        // The ref also blocks a second click that lands before the re-render.
        if (!canSubmit || businessId == null || inFlight.current) return;
        inFlight.current = true;
        setSaving(true);
        try {
            await apiService.submitForReview(businessId, { termsAccepted: true, privacyAccepted: true });
        } catch (err) {
            // Batch B (AB#2176) — the server's minimum-setup check names the missing piece.
            const fields = (err as { response?: { data?: { details?: { fields?: Record<string, unknown> } } } })
                ?.response?.data?.details?.fields;
            // Name the staff gap only when it is the sole missing item; otherwise the
            // generic message (which asks the owner to check every detail).
            const missing = Object.keys(fields ?? {});
            const onlyStaff = missing.length > 0 && missing.every(k => k.toLowerCase() === 'staff');
            showToast(t(onlyStaff ? 'mybiz.submit.staffMissing' : 'mybiz.submit.failed'), 'error');
            inFlight.current = false;
            setSaving(false);
            return;
        }
        showToast(t('mybiz.submit.success'), 'success');
        setSubmitted(true);
        try {
            // Picks up PendingApproval: this card hides and the owner-wide banner shows.
            await refreshOnboardingStatus();
        } catch {
            // The submit succeeded; the local pending state stays until the next status load.
        }
    };

    return (
        <section className={sectionStyles.card} aria-labelledby="submit-to-devanza-title">
            <h2 id="submit-to-devanza-title" className={sectionStyles.cardTitle}>{t('mybiz.submit.title')}</h2>
            <p className={sectionStyles.cardSub}>{t('mybiz.submit.body')}</p>

            <ul className={styles.checklist}>
                {CHECKLIST.map(item => {
                    const state = itemState[item.key];
                    return (
                        <li key={item.key} className={`${styles.checkItem} ${state === 'done' ? styles.checkItemDone : ''}`}>
                            <span className={styles.checkIcon} aria-hidden="true">{ITEM_STATE_ICON[state]}</span>
                            <span>{t(item.labelKey)}</span>
                            <span className={styles.srOnly}>{t(ITEM_STATE_KEY[state])}</span>
                        </li>
                    );
                })}
            </ul>
            {checkFailed && (
                <p className={styles.hint} role="alert">
                    {t('mybiz.submit.checkFailed')}
                    <button
                        type="button"
                        className={styles.retryBtn}
                        onClick={retryChecks}
                        // Only the branch refetch this button starts can block it; a
                        // branch fetch running for another reason must not.
                        disabled={branchState === 'unknown' && branchQuery.isFetching}
                        aria-busy={branchState === 'unknown' && branchQuery.isFetching}
                    >
                        {t('mybiz.submit.retry')}
                    </button>
                </p>
            )}
            <p className={styles.hint}>{t('mybiz.submit.pinHint')}</p>

            <label className={styles.consent}>
                <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                />
                <span>{t('mybiz.submit.consent')}</span>
            </label>
            <p className={styles.legalLinks}>
                <a href={legalDocumentUrl('terms', language)} target="_blank" rel="noopener noreferrer">
                    {t('mybiz.submit.termsLink')}
                </a>
                <span aria-hidden="true">·</span>
                <a href={legalDocumentUrl('privacy', language)} target="_blank" rel="noopener noreferrer">
                    {t('mybiz.submit.privacyLink')}
                </a>
            </p>

            <div className={sectionStyles.saveRow}>
                <span className={sectionStyles.saveHint}>{saveHintKey ? t(saveHintKey) : ''}</span>
                <button
                    type="button"
                    className={`${sectionStyles.btnPrimary} ${styles.btnDisabled}`}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                >
                    {saving ? t('mybiz.submit.saving') : t('mybiz.submit.action')}
                </button>
            </div>
        </section>
    );
};

export default SubmitToDevanzaCard;
